/**
 * attachmentQueue.ts
 *
 * Fetches attachment bytes for the current mail item with bounded
 * concurrency. 3 concurrent `getAttachmentContentAsync` calls is a hard
 * platform limit on web / new Outlook — going over it silently fails
 * calls rather than queueing them, so this is not a tunable knob.
 *
 * Attachments Office.js can't return as Base64 (cloud/URL attachments,
 * e.g. a OneDrive link) are recorded as skipped with a reason rather than
 * thrown — one unfileable attachment should never block filing the rest
 * of the email.
 */
import type { MailItemAttachmentMeta, MailItemLike } from "./officeShim";

const MAX_CONCURRENT = 3;

export interface FetchedAttachment {
  name: string;
  contentType: string;
  size: number;
  isInline: boolean;
  contentId: string | null;
  base64: string;
}

export interface SkippedAttachment {
  name: string;
  skippedReason: string;
}

export interface AttachmentQueueResult {
  fetched: FetchedAttachment[];
  skipped: SkippedAttachment[];
}

type FetchOneResult =
  | { kind: "fetched"; value: FetchedAttachment }
  | { kind: "skipped"; value: SkippedAttachment };

export async function fetchAttachments(
  item: MailItemLike,
): Promise<AttachmentQueueResult> {
  const attachments = item.attachments;
  const results: (FetchOneResult | undefined)[] = new Array(
    attachments.length,
  );

  let cursor = 0;
  async function worker(): Promise<void> {
    for (;;) {
      const index = cursor;
      cursor += 1;
      if (index >= attachments.length) {
        return;
      }
      const meta = attachments[index];
      if (!meta) {
        continue;
      }
      results[index] = await fetchOne(item, meta);
    }
  }

  const workerCount = Math.min(MAX_CONCURRENT, attachments.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  const fetched: FetchedAttachment[] = [];
  const skipped: SkippedAttachment[] = [];
  for (const result of results) {
    if (result === undefined) {
      continue;
    }
    if (result.kind === "fetched") {
      fetched.push(result.value);
    } else {
      skipped.push(result.value);
    }
  }
  return { fetched, skipped };
}

async function fetchOne(
  item: MailItemLike,
  meta: MailItemAttachmentMeta,
): Promise<FetchOneResult> {
  try {
    const content = await item.getAttachmentContentAsync(meta.id);
    if (!content.formatIsBase64) {
      return {
        kind: "skipped",
        value: {
          name: meta.name,
          skippedReason: "not filed: cloud attachment",
        },
      };
    }
    return {
      kind: "fetched",
      value: {
        name: meta.name,
        contentType: meta.contentType,
        size: meta.size,
        isInline: meta.isInline,
        contentId: meta.contentId,
        base64: content.content,
      },
    };
  } catch (err) {
    return {
      kind: "skipped",
      value: {
        name: meta.name,
        skippedReason:
          err instanceof Error ? err.message : "attachment fetch failed",
      },
    };
  }
}
